import { z } from 'zod';

import { fg } from '@atlaskit/platform-feature-flags';

import { isEnglishLocale } from '../locale';

/**
 * Ghost text experiment cohort enum
 */
export enum GhostTextExperimentCohort {
	CONTROL = 'control',
	THREE_P = '3p',
}

/**
 * Number of times to show each text before rotating to the next
 */
export const ROTATION_THRESHOLD = 3;

/**
 * Ghost text data schema for QuickFind search input placeholder.
 * Used to persist and restore dynamic placeholder text across sessions.
 */
export const ghostTextDataSchema = z.object({
	/** The placeholder text to display */
	text: z.string(),
	/** Number of times this text has been shown (0-2, resets after 3) */
	occurrence: z.number().optional().default(0),
});

/**
 * Schema for ghost text data with locale.
 * Used to persist and restore dynamic placeholder text across sessions.
 */
export const GhostTextDataV2Schema = z.object({
	/** Unique key identifying the current ghost text set */
	id: z.string(),
	/** Locale of the ghost text */
	locale: z.string(),
	/** The placeholder text to display */
	text: z.string(),
	/** Number of times this text has been shown (0-2, resets after 3) */
	occurrence: z.number().optional().default(0),
});

export type GhostTextData = z.infer<typeof ghostTextDataSchema>;

export type GhostTextDataV2 = z.infer<typeof GhostTextDataV2Schema>;

export type GhostTextRotation = Pick<GhostTextDataV2, 'id' | 'text'>;

export const isGhostTextDataV2 = (data: unknown): data is GhostTextDataV2 => {
	try {
		const result = GhostTextDataV2Schema.safeParse(data);
		return result.success;
	} catch {
		return false;
	}
};

/**
 * Parse ghost text data from a JSON string
 * Validates the data against ghostTextDataSchema, checks locale
 *
 * @param customPlaceholder - JSON string containing ghost text data
 * @param locale - Only returns data for English locales
 * @returns Parsed GhostTextData or undefined if invalid/empty/non-English locale
 */
export const parseGhostTextData = (
	customPlaceholder: string | undefined,
	locale: string,
): GhostTextData | GhostTextDataV2 | undefined => {
	if (!fg('quickfind-ghosttext')) {
		return undefined;
	}

	if (!customPlaceholder) {
		return undefined;
	}

	if (!isEnglishLocale(locale) && !fg('confluence_quick_find_ghost_text_i18n')) {
		return undefined;
	}

	if (fg('confluence_quick_find_ghost_text_i18n')) {
		try {
			const result = GhostTextDataV2Schema.safeParse(JSON.parse(customPlaceholder));
			if (result.success) {
				return result.data;
			}
			return undefined;
		} catch {
			return undefined;
		}
	}

	try {
		const result = ghostTextDataSchema.safeParse(JSON.parse(customPlaceholder));
		if (result.success) {
			return result.data;
		}
		return undefined;
	} catch {
		return undefined;
	}
};
